import { motion } from 'framer-motion';
import { useState, useEffect, useRef, useMemo } from 'react';

interface AudioLevelBarsProps {
  audioLevel: number;
  color?: string;
}

const BAR_CONFIGS = [
  { baseScale: 0.7, frequency: 1.2, phase: 0, noiseAmount: 0.15 },
  { baseScale: 1.0, frequency: 0.8, phase: 1.5, noiseAmount: 0.1 },
  { baseScale: 0.8, frequency: 1.5, phase: 3.0, noiseAmount: 0.15 },
];

const MIN_HEIGHT = 4;
const MAX_HEIGHT = 18;
const BAR_WIDTH = 3;
const GAP = 2;

export function AudioLevelBars({ audioLevel, color = '#60A5FA' }: AudioLevelBarsProps) {
  const [time, setTime] = useState(0);
  const animationRef = useRef<number | null>(null);
  const isActive = audioLevel >= 0.01;

  useEffect(() => {
    if (!isActive) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const animate = () => {
      setTime((t) => t + 0.08);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isActive]);

  const heights = useMemo(() => {
    if (!isActive) {
      return [MIN_HEIGHT, MIN_HEIGHT, MIN_HEIGHT];
    }

    const level = Math.max(0, Math.min(1, audioLevel));
    const baseHeight = MIN_HEIGHT + (MAX_HEIGHT - MIN_HEIGHT) * level;

    return BAR_CONFIGS.map((config) => {
      const noise = Math.sin(time * config.frequency + config.phase) * config.noiseAmount;
      const scale = config.baseScale + noise;
      return Math.max(MIN_HEIGHT, baseHeight * scale);
    });
  }, [isActive, audioLevel, time]);

  return (
    <div className="flex items-center justify-center" style={{ gap: GAP, height: 22 }}>
      {heights.map((height, index) => (
        <motion.div
          key={index}
          animate={{ height }}
          transition={{ type: 'spring', stiffness: 300, damping: 20, mass: 0.5 }}
          style={{
            width: BAR_WIDTH,
            backgroundColor: color,
            borderRadius: BAR_WIDTH,
          }}
        />
      ))}
    </div>
  );
}
